/**
 * CRM: everything HubSpot did for us, built on the leads table.
 *
 * - Outbound prospecting: a researched target list (imported from CSV or added by hand), more than
 *   one contact per company, and the four-touch sequence across email and LinkedIn.
 * - Partners: who introduces clients, and whether they still count as a partner.
 * - Reporting: the pipeline numbers (touches, replies, calls, new clients, partners) month by month.
 * - Export: every lead and contact as CSV.
 *
 * A "touch" is a researched approach to a company: more than one person, more
 * than one channel, followed up four times. Here a company's first touch counts it as approached.
 */
import { PRICE_BANDS } from "./pricing.ts";
import { ORDER_BANDS, SEQUENCE_LENGTH, isOpen, triage, type LeadPatch, type LeadRow, type LegalForm, type OrderBand } from "./leads.ts";
export type { LegalForm } from "./leads.ts";
import { addWorkingDays, londonDate } from "./working-days.ts";

export type TouchChannel = "email" | "linkedin" | "call" | "other";
export type TouchOutcome = "sent" | "replied" | "bounced" | "opted_out";
export type PartnerKind = "3pl" | "packaging_supplier" | "agency" | "accountant" | "carrier_broker" | "other";

export interface Contact {
  id?: string;
  lead_id: string;
  name: string;
  role: string | null;
  email: string | null;
  linkedin: string | null;
  phone: string | null;
  is_primary: boolean;
  do_not_contact: boolean;
  created_at?: string;
}

export interface Touch {
  id?: string;
  lead_id: string;
  contact_id: string | null;
  touch_no: number;
  channel: TouchChannel;
  outcome: TouchOutcome;
  at: string;
  by_email?: string | null;
  note: string | null;
}

export interface Partner {
  id?: string;
  name: string;
  kind: PartnerKind;
  contact_name: string | null;
  email: string | null;
  website: string | null;
  notes: string | null;
  owner: string | null;
  created_at?: string;
}

export const CHANNEL_LABEL: Record<TouchChannel, string> = { email: "Email", linkedin: "LinkedIn", call: "Call", other: "Other" };
export const OUTCOME_LABEL: Record<TouchOutcome, string> = { sent: "Sent, no reply yet", replied: "They replied", bounced: "Bounced or wrong person", opted_out: "Asked not to be contacted" };
export const PARTNER_KIND_LABEL: Record<PartnerKind, string> = {
  "3pl": "3PL or fulfilment house", packaging_supplier: "Packaging supplier", agency: "E-commerce agency",
  accountant: "Accountant", carrier_broker: "Carrier broker", other: "Other",
};

// ----------------------------------------------------------------------------- the sequence

/**
 * The four-touch sequence: what each touch is and how many working days after the previous one it
 * goes. The CEO owns the wording (task S03); change the timing here only.
 */
export const SEQUENCE: { no: number; channel: TouchChannel; afterWorkingDays: number; what: string }[] = [
  { no: 1, channel: "email", afterWorkingDays: 0, what: "Email: lead with shipping cost and one thing you noticed about how they ship" },
  { no: 2, channel: "linkedin", afterWorkingDays: 3, what: "LinkedIn: connect with a short note, to a second person if there is one" },
  { no: 3, channel: "email", afterWorkingDays: 4, what: "Email: a specific example from their site (parcel size, carrier, delivery page)" },
  { no: 4, channel: "email", afterWorkingDays: 7, what: "Email: a short last note, leaving the door open" },
];
if (SEQUENCE.length !== SEQUENCE_LENGTH) throw new Error("SEQUENCE and SEQUENCE_LENGTH disagree");
/** After the last touch, wait this long for a reply, then close as no response. */
export const CLOSE_AFTER_WORKING_DAYS = 7;

/** The date the next touch is due after touch `done` was sent at `at`, or the close date after the last one. */
export function nextTouchDue(done: number, at: Date): string {
  const day = londonDate(at);
  const next = SEQUENCE.find((s) => s.no === done + 1);
  return addWorkingDays(day, next ? next.afterWorkingDays : CLOSE_AFTER_WORKING_DAYS);
}

/** How the lead changes when a touch is logged. */
export function touchPatch(lead: Pick<LeadRow, "status" | "touch_count" | "replied_at">, t: Pick<Touch, "touch_no" | "outcome" | "at">): LeadPatch {
  const at = new Date(t.at);
  const iso = at.toISOString();
  const count = Math.max(lead.touch_count ?? 0, t.touch_no);
  const base: LeadPatch = { touch_count: count, last_touch_at: iso, last_contact_at: iso };
  switch (t.outcome) {
    case "replied":
      return { ...base, status: isOpen(lead.status) && (lead.status === "prospect" || lead.status === "contacted") ? "replied" : lead.status, replied_at: lead.replied_at ?? iso, next_touch_due: null };
    case "opted_out":
      return { ...base, status: "lost", closed_reason: "Asked not to be contacted", do_not_contact: true, next_touch_due: null, next_step: null, next_step_due: null };
    case "bounced":
      return { ...base, status: lead.status === "prospect" ? "contacted" : lead.status, next_step: "Find the right person and resend", next_step_due: londonDate(at) };
    default:
      return { ...base, status: lead.status === "prospect" ? "contacted" : lead.status, next_touch_due: nextTouchDue(count, at) };
  }
}

/**
 * How the lead changes when a touch is deleted (logged by mistake): the count and dates come from
 * the touches left. Stage, reply and opt-out stay as they are; change those under Progress.
 */
export function touchesLeftPatch(lead: Pick<LeadRow, "status" | "next_touch_due">, left: Pick<Touch, "touch_no" | "at">[]): LeadPatch {
  const count = left.reduce((m, t) => Math.max(m, t.touch_no), 0);
  const last = left.reduce<string | null>((m, t) => (!m || t.at > m ? t.at : m), null);
  const inSequence = lead.status === "prospect" || lead.status === "contacted";
  return {
    touch_count: count,
    last_touch_at: last ? new Date(last).toISOString() : null,
    next_touch_due: !inSequence ? lead.next_touch_due : last ? nextTouchDue(count, new Date(last)) : londonDate(new Date()),
  };
}

/** The next touch number for a lead. */
export const nextTouchNo = (lead: Pick<LeadRow, "touch_count">) => Math.min((lead.touch_count ?? 0) + 1, 20);

/**
 * A draft for an outbound touch, to edit before sending. Placeholders are in [square brackets].
 * Never estimate their saving before we have their data, and never ask for files.
 */
export const LEGAL_FORM_LABEL: Record<LegalForm, string> = {
  limited: "Limited company",
  llp: "LLP",
  sole_trader: "Sole trader",
  partnership: "Partnership (not an LLP)",
  other: "Other corporate body (charity, CIC, public body)",
};

/**
 * PECR (TASKS.md P16): a business-to-business marketing email with an opt-out is allowed to a limited
 * company, an LLP or another corporate body. Sole traders and partnerships count as individuals and need
 * consent first, so they get LinkedIn, a call or the free check instead. Unknown: check before emailing.
 */
export function coldEmailRule(form: LegalForm | null | undefined): { allowed: boolean; check: boolean; why: string | null } {
  if (form === "sole_trader" || form === "partnership")
    return { allowed: false, check: false, why: `${form === "sole_trader" ? "Sole traders" : "Partnerships"} need consent before a marketing email (PECR). Use LinkedIn, a call or the free check instead.` };
  if (!form) return { allowed: true, check: true, why: "Legal form not recorded: check it on Companies House before the first email. Sole traders and partnerships need consent first." };
  return { allowed: true, check: false, why: null };
}

/**
 * Questions for every discovery call (Company Plan, Sections 1.3 and 1.6). The cross-border and packaging rule
 * answers decide which checks the audit runs (TASKS.md P18); the 3PL answers test the proposed tender service.
 */
export const CALL_QUESTIONS: { topic: string; questions: string[] }[] = [
  { topic: "Fit", questions: [
    "Orders a month, and how many items in a typical order?",
    "Which carriers, and roughly what you spend with them a month?",
  ] },
  { topic: "Selling abroad", questions: [
    "Do you sell to the EU? About how many orders a month? Each low-value parcel now carries the EU's €3 duty.",
    "Who pays the duty and VAT: you at checkout (DDP) or the customer on delivery (DAP)? Are you registered for IOSS?",
    "Do you sell to the US, and how have you handled duty since the US ended its duty-free limit?",
    "Do you import stock in parcels under £135? That UK relief ends by October 2028.",
  ] },
  { topic: "Packaging rules", questions: [
    "Is turnover over £1 million, and do you place over 25 tonnes of packaging a year? Then packaging EPR reporting applies.",
  ] },
  { topic: "Fulfilment", questions: [
    "Do you pack in-house or use a fulfilment company (3PL)?",
    "If a 3PL: how did you choose it, and when does the contract end?",
  ] },
];

/** The draft for a touch. Email touches to a sole trader or partnership get no draft (`blocked`), only the reason. */
export function draftTouch(lead: Pick<LeadRow, "company" | "website"> & { legal_form?: LegalForm | null }, contactName: string | null, touchNo: number, sender = "[Your name]"): { subject: string; body: string; blocked?: boolean } {
  const first = contactName?.trim().split(/\s+/)[0] ?? "[First name]";
  const company = lead.company ?? lead.website ?? "[Company]";
  const rule = coldEmailRule(lead.legal_form);
  const channel = SEQUENCE.find((s) => s.no === touchNo)?.channel ?? "email";
  if (!rule.allowed && channel === "email") return { subject: "", body: `No cold email to ${company}. ${rule.why}`, blocked: true };
  const sign = `${sender}\nRyoma Insight\nbusiness@ryoma-insight.co.uk`;
  const bodies: Record<number, { subject: string; body: string }> = {
    1: {
      subject: `${company}: what your parcels cost to ship`,
      body: `Hello ${first},\n\n[One line on what you noticed: their parcel sizes, carriers or delivery page.]\n\nWe are an engineering-led consultancy that cuts what online sellers spend getting an order into a box and out of the door. Most sellers we look at pay to ship empty space without knowing it.\n\nWould a 30-minute call be useful to see whether that applies to ${company}?\n\n[Where we found your details: your website, LinkedIn or Apollo.io.] If you would rather not hear from us, just say so and we will not contact you again.\n\nBest wishes,\n${sign}`,
    },
    2: {
      subject: "LinkedIn note",
      body: `Hi ${first}, I help online sellers cut what they spend on shipping and packaging. [One line about ${company}.] Happy to connect.`,
    },
    3: {
      subject: `${company}: one example`,
      body: `Hello ${first},\n\n[A specific example: a product that likely ships in a box bigger than it needs, a carrier band edge, or a delivery promise that costs more than it should.]\n\nIf it would help, we can talk it through in 30 minutes. There is nothing to prepare. If you would rather not hear from us, just say so.\n\nBest wishes,\n${sign}`,
    },
    4: {
      subject: `Closing the loop, ${company}`,
      body: `Hello ${first},\n\nI will leave it here for now. If shipping costs come up, for example at a carrier renewal, just reply to this email.\n\nBest wishes,\n${sign}`,
    },
  };
  return bodies[touchNo] ?? bodies[4]!;
}

// ----------------------------------------------------------------------------- import

const FREE_MAIL = new Set(["gmail.com", "googlemail.com", "hotmail.com", "hotmail.co.uk", "outlook.com", "live.com", "live.co.uk", "yahoo.com", "yahoo.co.uk", "icloud.com", "me.com", "aol.com", "btinternet.com", "proton.me", "protonmail.com"]);

/** "https://www.Shop.co.uk/about" → "shop.co.uk"; "jo@shop.co.uk" → "shop.co.uk"; free email domains → null. */
export function domainOf(v: string | null | undefined): string | null {
  if (!v) return null;
  let t = v.trim().toLowerCase();
  if (t.includes("@")) t = t.split("@").pop()!;
  t = t.replace(/^[a-z]+:\/\//, "").replace(/^www\./, "").split(/[/?#]/)[0]!.replace(/:\d+$/, "");
  if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(t) || FREE_MAIL.has(t)) return null;
  return t;
}

/** An order band from "2,500", "2500", "1,000 to 2,999" or "1000-2999". */
export function bandFromText(v: string | null | undefined): OrderBand | null {
  if (!v) return null;
  const t = v.trim();
  if ((ORDER_BANDS as readonly string[]).includes(t)) return t as OrderBand;
  const nums = t.replace(/,/g, "").match(/\d+/g)?.map(Number) ?? [];
  if (!nums.length) return null;
  const n = nums.length > 1 ? (nums[0]! + nums[1]!) / 2 : nums[0]!;
  if (n < 300) return "Under 300";
  if (n < 1000) return "300 to 999";
  if (n < 3000) return "1,000 to 2,999";
  if (n < 8000) return "3,000 to 7,999";
  if (n < 20000) return "8,000 to 19,999";
  return "20,000 or more";
}

export interface ImportProspect {
  company: string;
  website: string | null;
  orders_band: OrderBand | null;
  sector: string | null;
  notes: string | null;
  contacts: Omit<Contact, "lead_id">[];
}

/** Where an imported list came from, when the file shows it. */
export interface ImportSource { channel: "cold_email"; detail: string }

const HEADER_ALIASES: Record<string, string[]> = {
  company: ["company", "company name", "business", "brand", "seller"],
  website: ["website", "site", "url", "domain", "shop"],
  orders: ["orders", "orders a month", "orders per month", "monthly orders", "order volume", "estimated orders"],
  sector: ["sector", "category", "industry"],
  name: ["contact", "contact name", "name", "full name", "person"],
  first: ["first name", "firstname"],
  last: ["last name", "lastname", "surname"],
  role: ["role", "job title", "title", "position"],
  email: ["email", "email address", "work email"],
  linkedin: ["linkedin", "linkedin url", "linkedin profile", "person linkedin url"],
  notes: ["notes", "note", "research", "why them"],
};

/** Minimal CSV parsing with quotes, commas and new lines in fields. */
export function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  const t = text.replace(/^﻿/, "");
  for (let i = 0; i < t.length; i++) {
    const c = t[i]!;
    if (quoted) {
      if (c === '"' && t[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') quoted = false;
      else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && t[i + 1] === "\n") i++;
      row.push(field); field = "";
      if (row.some((x) => x.trim())) rows.push(row);
      row = [];
    } else field += c;
  }
  row.push(field);
  if (row.some((x) => x.trim())) rows.push(row);
  return rows;
}

/**
 * A target list as prospects. One row per contact; rows with the same company (or website) become
 * one prospect with several contacts. Needs at least a company column.
 */
export function parseTargetList(text: string): { prospects: ImportProspect[]; problems: string[]; source: ImportSource | null } {
  const rows = parseCsvText(text);
  const problems: string[] = [];
  if (rows.length < 2) return { prospects: [], problems: ["The file needs a header row and at least one company."], source: null };
  const header = rows[0]!.map((h) => h.trim().toLowerCase());
  const col: Record<string, number> = {};
  for (const [key, aliases] of Object.entries(HEADER_ALIASES)) {
    const i = header.findIndex((h) => aliases.includes(h));
    if (i >= 0) col[key] = i;
  }
  if (col.company === undefined) return { prospects: [], problems: ["No company column. Use the headings: company, website, orders, sector, contact name, role, email, linkedin, notes."], source: null };
  const get = (r: string[], k: string) => (col[k] !== undefined ? r[col[k]!]?.trim() || null : null);
  // An Apollo.io export: record where the details came from, so the first email can say so
  const source: ImportSource | null = header.some((h) => h.startsWith("apollo ") || h === "person linkedin url") ? { channel: "cold_email", detail: "Apollo.io export" } : null;
  const byKey = new Map<string, ImportProspect>();
  rows.slice(1).forEach((r, i) => {
    const company = get(r, "company");
    if (!company) { problems.push(`Row ${i + 2}: no company, skipped.`); return; }
    const website = get(r, "website");
    const key = domainOf(website) ?? company.toLowerCase();
    let p = byKey.get(key);
    if (!p) {
      p = { company: company.slice(0, 200), website: website?.slice(0, 200) ?? null, orders_band: bandFromText(get(r, "orders")), sector: get(r, "sector")?.slice(0, 100) ?? null, notes: get(r, "notes")?.slice(0, 2000) ?? null, contacts: [] };
      byKey.set(key, p);
    } else {
      p.orders_band ??= bandFromText(get(r, "orders"));
      p.sector ??= get(r, "sector");
    }
    const name = get(r, "name") ?? ([get(r, "first"), get(r, "last")].filter(Boolean).join(" ") || null);
    const email = get(r, "email")?.toLowerCase() ?? null;
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) problems.push(`Row ${i + 2}: "${email}" does not look like an email, left out.`);
    if (name) {
      p.contacts.push({
        name: name.slice(0, 200), role: get(r, "role")?.slice(0, 120) ?? null,
        email: email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null,
        linkedin: get(r, "linkedin")?.slice(0, 300) ?? null, phone: null,
        is_primary: p.contacts.length === 0, do_not_contact: false,
      });
    }
  });
  return { prospects: [...byKey.values()], problems, source };
}

/**
 * Whether a prospect is already on our books: same website or company, or a contact email we
 * already hold. Someone who asked not to be contacted is never added again.
 */
export function duplicateOf(p: Pick<ImportProspect, "company" | "website" | "contacts">, leads: LeadRow[], contacts: Pick<Contact, "lead_id" | "email">[] = []): { lead: LeadRow; reason: string } | null {
  const dom = domainOf(p.website);
  const emails = new Set(p.contacts.map((c) => c.email).filter(Boolean) as string[]);
  const emailDomains = new Set([...emails].map((e) => domainOf(e)).filter(Boolean) as string[]);
  for (const l of leads) {
    const ld = domainOf(l.website) ?? domainOf(l.company) ?? domainOf(l.email);
    const sameSite = !!ld && (ld === dom || emailDomains.has(ld));
    const sameName = !!l.company && l.company.trim().toLowerCase() === p.company.trim().toLowerCase();
    const sameEmail = (!!l.email && emails.has(l.email)) || contacts.some((c) => c.lead_id === l.id && c.email && emails.has(c.email));
    if (sameSite || sameName || sameEmail) {
      return { lead: l, reason: l.do_not_contact ? "asked not to be contacted" : `already a lead (${l.company ?? l.name})` };
    }
  }
  return null;
}

// ----------------------------------------------------------------------------- partners

/** A partner with no introduction in six months is a contact, not a partner. */
export const PARTNER_ACTIVE_MONTHS = 6;

export function partnerStanding(partner: Pick<Partner, "id" | "created_at">, leads: LeadRow[], now = new Date()) {
  const intros = leads.filter((l) => l.partner_id === partner.id);
  const last = intros.map((l) => l.received_at).sort().at(-1) ?? null;
  const cutoff = new Date(now);
  cutoff.setUTCMonth(cutoff.getUTCMonth() - PARTNER_ACTIVE_MONTHS);
  // A new partner gets six months from when we agreed to work together.
  const since = last ?? partner.created_at ?? null;
  const active = !!since && Date.parse(since) >= cutoff.getTime();
  return { intros: intros.length, clients: intros.filter((l) => l.status === "won").length, last, active };
}

// ----------------------------------------------------------------------------- reporting

/** "2026-10" → "FY1" (FY1 is April 2026 to March 2027). */
export function fyOf(month: string): string {
  const [y, m] = month.split("-").map(Number) as [number, number];
  const startYear = m >= 4 ? y : y - 1;
  return `FY${startYear - 2025}`;
}

const monthOf = (iso: string | null | undefined) => (iso ? londonDate(new Date(iso)).slice(0, 7) : null);

export interface MonthMetrics {
  month: string;
  fy: string;
  /** Companies approached for the first time (touch 1) */
  touches: number;
  /** All touches sent, including follow-ups */
  touchesSent: number;
  /** Outbound prospects who replied */
  replies: number;
  /** Website enquiries and fit checks, and inbound leads added by hand */
  enquiries: number;
  partnerIntros: number;
  calls: number;
  clients: number;
  clientsFromPartners: number;
  replyRate: number | null;
  callToClient: number | null;
}

export function monthMetrics(month: string, leads: LeadRow[], touches: Pick<Touch, "lead_id" | "touch_no" | "at">[]): MonthMetrics {
  const inMonth = (iso: string | null | undefined) => monthOf(iso) === month;
  const outbound = new Set(leads.filter((l) => l.origin === "outbound").map((l) => l.id));
  const firstTouches = new Set(touches.filter((t) => t.touch_no === 1 && inMonth(t.at) && outbound.has(t.lead_id)).map((t) => t.lead_id));
  const replies = leads.filter((l) => l.origin === "outbound" && inMonth(l.replied_at)).length;
  const enquiries = leads.filter((l) => l.origin === "inbound" && inMonth(l.received_at)).length;
  const partnerIntros = leads.filter((l) => l.origin === "partner" && inMonth(l.received_at)).length;
  const calls = leads.filter((l) => inMonth(l.call_done_at)).length;
  const won = leads.filter((l) => inMonth(l.won_at));
  const touchesN = firstTouches.size;
  const replyRate = touchesN ? replies / touchesN : null;
  const callToClient = calls ? won.length / calls : null;
  return {
    month, fy: fyOf(month), touches: touchesN, touchesSent: touches.filter((t) => inMonth(t.at)).length, replies, enquiries, partnerIntros,
    calls, clients: won.length, clientsFromPartners: won.filter((l) => l.origin === "partner").length, replyRate, callToClient,
  };
}

/** Reported audits, and how many went on to implementation. */
export function implementationRate(audits: { status: string; implementation_starts: string | null }[]): { reported: number; implemented: number } {
  const reported = audits.filter((a) => ["reported", "implementing", "closed"].includes(a.status)).length;
  const implemented = audits.filter((a) => a.implementation_starts).length;
  return { reported, implemented };
}

/** The audit fees open opportunities would bring, from their order band (quoted bands at the starting figure). */
export function pipelineValue(leads: LeadRow[]): { pence: number; counted: number; unknown: number } {
  let pence = 0, counted = 0, unknown = 0;
  for (const l of leads) {
    if (!isOpen(l.status) || l.do_not_contact) continue;
    const t = triage(l);
    if (t.fit === "audit" || t.fit === "quote") { pence += t.band?.feePence ?? 0; counted++; }
    else if (t.fit === "review") { pence += PRICE_BANDS[1]!.feePence; counted++; }
    else if (t.fit === "unknown") unknown++;
  }
  return { pence, counted, unknown };
}

// ----------------------------------------------------------------------------- export

export const csvCell = (v: unknown) => {
  const s = v === null || v === undefined ? "" : Array.isArray(v) ? v.join("; ") : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Every lead, one row per contact (or one row if it has none). */
export function leadsCsv(leads: LeadRow[], contacts: Contact[], partners: Partner[]): string {
  const partnerName = new Map(partners.map((p) => [p.id, p.name]));
  const header = ["Company", "Website", "Origin", "Stage", "Owner", "Orders a month", "Sector", "Partner", "Touches", "Last contact", "Next touch due", "Next step", "Next step due", "Do not contact", "Contact", "Role", "Email", "LinkedIn", "Received", "Source", "Source detail", "Tags"];
  const out = [header.map(csvCell).join(",")];
  for (const l of leads) {
    const cs = contacts.filter((c) => c.lead_id === l.id);
    const base = [l.company ?? "", l.website ?? "", l.origin, l.status, l.owner ?? "", l.orders_band ?? "", l.sector ?? "", l.partner_id ? partnerName.get(l.partner_id) ?? "" : "", l.touch_count ?? 0, l.last_contact_at?.slice(0, 10) ?? "", l.next_touch_due ?? "", l.next_step ?? "", l.next_step_due ?? "", l.do_not_contact ? "Yes" : ""];
    const rows = cs.length ? cs.map((c) => [c.name, c.role ?? "", c.email ?? "", c.linkedin ?? ""]) : [[l.name, "", l.email ?? "", ""]];
    const source = l.source_channel ?? (l.source === "enquiry" || l.source === "fit-check" ? "website" : l.origin === "partner" ? "partner" : "");
    for (const r of rows) out.push([...base, ...r, l.received_at.slice(0, 10), source, l.source_detail ?? "", (l.tags ?? []).join("; ")].map(csvCell).join(","));
  }
  return out.join("\r\n") + "\r\n";
}
